import * as React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
// hooks
import { useSnackbar } from 'notistack'
import { useMediaQuery } from '@mui/material'
import { useCurrentUser } from 'hooks/useCurrentUser'
// constants
import { SCREEN_SIZE } from 'constants/screenSize'
// components
import Input from 'components/FormElements/Input'
// themes
import colors from 'themes/colors'
// static
import EditIconOrigin from '@mui/icons-material/Edit'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// store
import { updateRiskTemplate as updateRiskTemplateAction } from 'store/reducers/riskTemplates'
// utils
import { size } from 'polished'
import { debounce } from 'lodash'

const InputContainer = styled.div`
  && {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;

    > div:nth-child(2) {
      margin-right: 12px;
    }

    input {
      font-size: 24px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > svg {
      min-height: 20px;
      min-width: 20px;
    }
  }
`

const EditIcon = styled(EditIconOrigin)`
  margin: 0 !important;
  ${size('20px')};
`

const EditName: React.FC = () => {
  const dispatch = useDispatch()

  const [value, setValue] = React.useState('')

  const { riskTemplate, updateRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { enqueueSnackbar } = useSnackbar()

  const {
    permissionsFromSecurityGroups: { manageRiskTemplates }
  } = useCurrentUser()

  const isTablet = useMediaQuery(SCREEN_SIZE.TABLET)

  const debouncedRiskTemplateUpdate = React.useCallback(
    debounce(async (value: string) => {
      const isUpdated = await updateRiskTemplate({ name: value })

      if (!isUpdated) {
        return
      }

      dispatch(updateRiskTemplateAction({ id: riskTemplate.id, data: { name: value } }))

      enqueueSnackbar('Form name updated!', {
        variant: 'success',
        autoHideDuration: 2000
      })
    }, 1000),
    []
  )

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist()

    const { value } = event.target

    setValue(value)
    debouncedRiskTemplateUpdate(value)
  }

  React.useEffect(() => {
    setValue(riskTemplate.name)
  }, [riskTemplate.name])

  if (isTablet) {
    return null
  }

  return (
    <InputContainer>
      {manageRiskTemplates && <EditIcon />}
      <Input
        fullWidth
        name='name'
        value={value}
        disableUnderline
        onChange={handleOnChange}
        inputColor={colors.white100}
        disabled={!manageRiskTemplates}
      />
    </InputContainer>
  )
}

export default EditName
